<template>
	<AppModal
		class="add-customer-modal"
		@hide-modal="
			$emit('hideModal');
			loadingStore.addingUserToDossierState = closeModal;
		"
	>
		<template #header>Klant toegang geven tot dossier</template>

		<AppModalContent>
			<AddUserModalTable :users="dossierAccessList ?? []" />
			<div ref="modalForm" class="add-customer-form text-sm">
				<div>
					<label for="fullname-input"> Naam klant </label>
					<InputText id="fullname-input" v-model="state.fullname" />
				</div>

				<div>
					<label for="email-input"> E-mailadres klant </label>
					<InputText id="email-input" v-model="state.email" />
				</div>

				<AppLabel :label="resultLabel" />
				<div class="loading-feedback-container">
					<span class="flex-item">{{
						loadingStore.addingUserToDossierState
					}}</span>
				</div>
			</div>
		</AppModalContent>

		<template #footer>
			<AppButton variant="primary" class="self-end" @click="addUserToDossier">
				Geef toegang
			</AppButton>
		</template>
	</AppModal>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch, onMounted } from "vue";
import useAddCustomerToDossier from "@/store/useAddUserToDossier";
import { useLoadingStore } from "@/store/useLoadingStore";
import { AddCustomerToDossierState } from "@/enums/AddCustomerToDossierState";
import { LabelType } from "@/enums/LabelType";
import { useDossierStore } from "@/store/useDossierStore";
import { api } from "@/loanLinkApi";
import { isKeyOf } from "@/helpers/typeHelpers";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";

defineEmits<{
	hideModal: [];
}>();

const dossierStore = useDossierStore();

const { data: dossierAccessList } = useQuery(() =>
	queries.dossier.dossierUsers(dossierStore.selectedDossier?.id ?? ""),
);

const loadingStore = useLoadingStore();
const { state } = useAddCustomerToDossier();
const closeModal = AddCustomerToDossierState.SAVED;

const modal = useTemplateRef("modalForm");

onMounted(() => {
	modal.value?.querySelector("input")?.focus();
});

const resultLabel = ref<{
	message: string;
	type: LabelType;
} | null>(null);

watch(
	state,
	() => {
		resultLabel.value = null;
	},
	{ deep: true },
);

/**
 * Manages form validation and adding a user to a dossier.
 */
async function addUserToDossier() {
	resultLabel.value = null;

	const selectedDossier = dossierStore.selectedDossier;
	const fullName = state.value.fullname;
	const emailAddress = state.value.email;

	if (!selectedDossier) {
		resultLabel.value = {
			message: "Selecteer een dossier",
			type: LabelType.Error,
		};
		return;
	}

	if (!fullName || fullName?.length <= 0) {
		resultLabel.value = {
			message: "Vul een klantnaam in",
			type: LabelType.Error,
		};
		return;
	}
	if (!emailAddress || emailAddress?.length <= 0) {
		resultLabel.value = {
			message: "Vul een e-mailadres in",
			type: LabelType.Error,
		};
		return;
	}

	loadingStore.addingUserToADossier = true;

	const dossierId = selectedDossier.id;

	try {
		resultLabel.value = {
			message: "Klant wordt aan dossier toegevoegd...",
			type: LabelType.Neutral,
		};

		const res = await api.user.createUser({
			dossierId,
			customerEmail: emailAddress,
			customerName: fullName,
		});

		if (!res.ok) {
			const errorMessage = {
				message: "Er is een onbekende fout opgetreden",
				type: LabelType.Error,
			};

			const messages = {
				"user/user-already-has-access":
					"Deze klant heeft al toegang tot het dossier",
				"dossier/not-found": "Dossier niet gevonden",
				"user/access-not-allowed":
					"U kan geen klanten toevoegen aan dit dossier",
				"user/invalid-email": "Vul een geldig e-mailadres in",
			};

			const errorCode = "code" in res.data ? res.data.code : null;

			if (typeof errorCode === "string" && isKeyOf(messages, errorCode)) {
				errorMessage.message = messages[errorCode];
			}

			resultLabel.value = errorMessage;
		} else {
			resultLabel.value = {
				message: "Klant toegevoegd aan dossier",
				type: LabelType.Success,
			};

			setTimeout(() => {
				resultLabel.value = null;
			}, 2000);
		}
	} catch (e) {
		resultLabel.value = {
			message: "Er is een onbekende fout opgetreden",
			type: LabelType.Error,
		};
		throw e;
	} finally {
		loadingStore.addingUserToADossier = false;
	}
}
</script>

<style lang="scss">
.add-customer-modal {
	.add-customer-form {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;

		.loading-feedback-container {
			display: flex;
			flex-flow: row wrap;
			font-size: 0.85rem;
			color: var(--accent-primary);
		}
	}
}
</style>
